import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { BOARDS, getCell } from "../data/boards";
import type { IconRef, SentenceToken } from "../game/types";
import { iconKey } from "../game/types";
import Frame from "./Frame";
import type { Lang } from "../i18n";

interface Props {
  cellRef: IconRef;
  token?: SentenceToken;
  onClose: () => void;
}

export default function IconZoom({ cellRef, token, onClose }: Props) {
  const { i18n, t } = useTranslation();
  const lang = (i18n.resolvedLanguage as Lang) ?? "en";
  const cell = getCell(cellRef.board, cellRef.col, cellRef.row);
  const board = BOARDS[cellRef.board - 1];
  const key = iconKey(cellRef);
  const src = `/boards/board-${cellRef.board}-${cellRef.col}${cellRef.row}.png`;
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="zoom-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="zoom-card" onClick={(e) => e.stopPropagation()}>
        <button className="zoom-close" onClick={onClose} aria-label="Close">×</button>

        <div className="zoom-meta">
          <span className="pill accent">{key}</span>
          <span className="pill">Board {board?.id} · {board?.theme[lang] ?? board?.theme.en}</span>
          {cell?.dualImage && <span className="pill">split cell — X selects right image</span>}
          {token && <span className="pill">word #{token.order}</span>}
        </div>

        <div className="zoom-image">
          <img src={src} alt={key} />
          {token && <Frame token={token} />}
        </div>

        {token && (
          <div className="zoom-markers">
            <div className="zoom-marker">
              <span className="zoom-marker__label">Part of speech</span>
              <span className={`zoom-marker__pill ${token.frameColor}`}>{t(`pos.${token.frameColor}`)}</span>
            </div>
            <div className="zoom-marker">
              <span className="zoom-marker__label">Number</span>
              <span className="zoom-marker__value">{token.plural ? "Plural (double border)" : "Singular"}</span>
            </div>
            {token.tense ? (
              <div className="zoom-marker">
                <span className="zoom-marker__label">Tense</span>
                <span className="zoom-marker__value">{t(`tense.${token.tense}`)}</span>
              </div>
            ) : token.frameColor === "verb" ? (
              <div className="zoom-marker">
                <span className="zoom-marker__label">Tense</span>
                <span className="zoom-marker__value">{t("tense.infinitive")}</span>
              </div>
            ) : null}
            {token.gender && (
              <div className="zoom-marker">
                <span className="zoom-marker__label">Gender</span>
                <span className="zoom-marker__value">{t(`gender.${token.gender}`)}</span>
              </div>
            )}
            {token.negation && (
              <div className="zoom-marker">
                <span className="zoom-marker__label">Negation</span>
                <span className="zoom-marker__value">✕ — negated{cell?.dualImage ? " / use X-marked image" : ""}</span>
              </div>
            )}
            {token.compoundWithNext && (
              <div className="zoom-marker">
                <span className="zoom-marker__label">Compound</span>
                <span className="zoom-marker__value">+ join with the next icon (read as one word)</span>
              </div>
            )}
          </div>
        )}

        {!revealed ? (
          <button className="btn ghost" onClick={() => setRevealed(true)}>
            Reveal translations
          </button>
        ) : (
          <>
            <div className="zoom-glosses">
              {(["en", "it", "de", "fr"] as Lang[]).map((lng) => (
                <div key={lng} className="zoom-gloss-row">
                  <span className="zoom-gloss-lang">{lng.toUpperCase()}</span>
                  <span>{cell?.gloss?.[lng] ?? "—"}</span>
                </div>
              ))}
            </div>
            <button className="btn ghost" onClick={() => setRevealed(false)}>Hide translations</button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
